import { NavLink } from 'react-router-dom';
import { AtlasLogo, Icon } from '@/ui';
import { useMe } from '@/hooks/useMe';
import { visibleNav, isNavEnabled } from './nav';

// Persistent left navigation. Active state is driven by React Router's
// NavLink isActive — keeps the route the single source of truth.
export function Sidebar() {
  const { data: me } = useMe();
  const isAdmin = (me?.role ?? '').toLowerCase() === 'admin';
  const items = visibleNav(isAdmin);

  return (
    <aside
      style={{
        width: 232,
        flexShrink: 0,
        background: 'var(--apm-bg-2)',
        borderRight: '1px solid var(--apm-line)',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      <div
        style={{
          padding: '26px 18px',
          borderBottom: '1px solid var(--apm-line)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              // Box always uses --apm-ink, which flips per theme:
              //   light themes → dark box
              //   dark theme   → light box
              background: 'var(--apm-ink)',
              // currentColor for the A pillar inside AtlasLogo — the
              // inverse of the box bg keeps the silhouette readable.
              color: 'var(--apm-bg-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <AtlasLogo size={22} />
          </div>
          <div>
            <div
              style={{
                fontFamily: 'var(--apm-font-display)',
                fontSize: 17,
                lineHeight: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Atlas
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--apm-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                marginTop: 3,
              }}
            >
              Earlens · APM
            </div>
          </div>
        </div>
      </div>

      <nav
        style={{
          flex: 1,
          padding: '12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowY: 'auto',
        }}
      >
        {items.map((item) => {
          // Focus mode: disabled items render as a static, grayed-out row
          // with no navigation. The link is intentionally broken so the tab
          // is visible but not yet usable.
          if (!isNavEnabled(item.id)) {
            return (
              <div
                key={item.id}
                aria-disabled="true"
                title="Coming soon"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'var(--apm-muted)',
                  opacity: 0.45,
                  fontFamily: 'var(--apm-font-body)',
                  fontSize: 13.5,
                  fontWeight: 500,
                  cursor: 'not-allowed',
                  position: 'relative',
                  userSelect: 'none',
                }}
              >
                <Icon
                  name={item.icon}
                  size={16}
                  style={{ color: 'var(--apm-muted)' }}
                />
                <span style={{ flex: 1 }}>{item.label}</span>
              </div>
            );
          }

          return (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === '/'}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 10px',
                borderRadius: 6,
                background: isActive ? 'var(--apm-bg-sunken)' : 'transparent',
                color: isActive ? 'var(--apm-ink)' : 'var(--apm-ink-2)',
                border: 'none',
                textAlign: 'left',
                fontFamily: 'var(--apm-font-body)',
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                position: 'relative',
                textDecoration: 'none',
              })}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 6,
                        bottom: 6,
                        width: 2,
                        background: 'var(--apm-accent)',
                        borderRadius: 2,
                      }}
                    />
                  )}
                  <Icon
                    name={item.icon}
                    size={16}
                    style={{
                      color: isActive ? 'var(--apm-accent)' : 'var(--apm-muted)',
                    }}
                  />
                  <span style={{ flex: 1 }}>{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
